import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export type McpAuthMode = 'caller_token' | 'stored_credentials';
export type CallerTokenIn = 'header' | 'body';
export type CredentialTemplate = 'key_pair' | 'single_token';

export interface McpAuthField {
  name: string;
  sendAs?: string;
  configured?: boolean;
}

export interface McpDiscoveredTool {
  name: string;
  description?: string;
}

export interface McpIntegration {
  mcpId: string;
  name: string;
  description: string | null;
  serverUrl: string;
  authMode: McpAuthMode;
  callerTokenIn?: CallerTokenIn | null;
  payloadParameter?: string | null;
  credentialTemplate?: CredentialTemplate | null;
  authFields?: McpAuthField[] | null;
  status: string;
  discoveredTools?: McpDiscoveredTool[] | null;
  lastVerifiedAt: string | null;
  orgId: string;
  agentIds: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CreateMcpIntegrationRequest {
  name: string;
  description?: string;
  server_url: string;
  auth_mode: McpAuthMode;
  caller_token_in?: CallerTokenIn;
  payload_parameter?: string;
  credential_template?: CredentialTemplate;
  credentials?: Record<string, string>;
}

export interface UpdateMcpIntegrationRequest {
  name?: string;
  description?: string;
  server_url?: string;
  caller_token_in?: CallerTokenIn;
  payload_parameter?: string;
  credentials?: Record<string, string>;
  rotate?: boolean;
}

export interface VerifyMcpIntegrationRequest {
  trial_token?: string;
  trial_credentials?: Record<string, string>;
  use_stored?: boolean;
}

export interface VerifyMcpResult {
  ok: boolean;
  mcpId?: string;
  tools?: McpDiscoveredTool[];
  lastVerifiedAt?: string | null;
  error?: string;
  message?: string;
}

export function mcpHost(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] || url;
  }
}

export function formatMcpToolLog(event: {
  type?: string;
  data?: Record<string, unknown>;
}): string {
  const data = event.data || {};
  const mcpId = String(data.mcp_id ?? data.mcpId ?? '');
  const tool = String(data.tool ?? '');
  const error = data.error ? String(data.error) : '';
  const duration =
    data.duration_ms != null ? `${data.duration_ms}ms` : '';
  const parts = [event.type || 'mcp.tool', mcpId, tool, duration, error].filter(
    Boolean,
  );
  return parts.join(' · ');
}

export const mcpIntegrationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMcpIntegrations: builder.query<McpIntegration[], { orgId: string } | void>({
      query: () => '/portal/mcp-integrations',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{
          mcpIntegrations: McpIntegration[];
        }>(response);
        return normalized.mcpIntegrations ?? [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((row) => ({
                type: 'McpIntegration' as const,
                id: row.mcpId,
              })),
              { type: 'McpIntegration', id: 'LIST' },
            ]
          : [{ type: 'McpIntegration', id: 'LIST' }],
    }),

    getMcpIntegration: builder.query<McpIntegration, string>({
      query: (id) => `/portal/mcp-integrations/${id}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<McpIntegration>(response),
      providesTags: (_r, _e, id) => [{ type: 'McpIntegration', id }],
    }),

    createMcpIntegration: builder.mutation<
      McpIntegration,
      CreateMcpIntegrationRequest
    >({
      query: (body) => ({
        url: '/portal/mcp-integrations',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<McpIntegration>(response),
      invalidatesTags: [{ type: 'McpIntegration', id: 'LIST' }],
    }),

    updateMcpIntegration: builder.mutation<
      McpIntegration,
      { id: string; body: UpdateMcpIntegrationRequest }
    >({
      query: ({ id, body }) => ({
        url: `/portal/mcp-integrations/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<McpIntegration>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'McpIntegration', id },
        { type: 'McpIntegration', id: 'LIST' },
      ],
    }),

    deleteMcpIntegration: builder.mutation<
      { deleted: boolean },
      { id: string; confirm?: boolean }
    >({
      query: ({ id, confirm = true }) => ({
        url: `/portal/mcp-integrations/${id}`,
        method: 'DELETE',
        body: { confirm },
      }),
      invalidatesTags: [{ type: 'McpIntegration', id: 'LIST' }, 'Agent'],
    }),

    verifyMcpIntegration: builder.mutation<
      VerifyMcpResult,
      { id: string; body?: VerifyMcpIntegrationRequest }
    >({
      query: ({ id, body }) => ({
        url: `/portal/mcp-integrations/${id}/verify`,
        method: 'POST',
        body: body || {},
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<VerifyMcpResult>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'McpIntegration', id },
        { type: 'McpIntegration', id: 'LIST' },
      ],
    }),

    updateMcpAgentAssociations: builder.mutation<
      McpIntegration,
      { id: string; agentIds: string[] }
    >({
      query: ({ id, agentIds }) => ({
        url: `/portal/mcp-integrations/${id}/agent-associations`,
        method: 'PUT',
        body: { agent_ids: agentIds },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<McpIntegration>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'McpIntegration', id },
        { type: 'McpIntegration', id: 'LIST' },
        'Agent',
      ],
    }),
  }),
});

export const {
  useGetMcpIntegrationsQuery,
  useGetMcpIntegrationQuery,
  useCreateMcpIntegrationMutation,
  useUpdateMcpIntegrationMutation,
  useDeleteMcpIntegrationMutation,
  useVerifyMcpIntegrationMutation,
  useUpdateMcpAgentAssociationsMutation,
} = mcpIntegrationsApi;
